import { Editor, useEditorState } from '@tiptap/react';
import { useMemo } from 'react';
import BoldIcon from './assets/bold.svg?react';
import CenteredIcon from './assets/centered.svg?react';
import ItalicIcon from './assets/italic.svg?react';
import LeftIcon from './assets/left.svg?react';
import ListIcon from './assets/list.svg?react';
import OrderedListIcon from './assets/ordered-list.svg?react';
import UnderlineIcon from './assets/underlined.svg?react';
export enum ToolType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST = 'LIST',
  CENTERED = 'CENTERED',
  LEFT = 'LEFT',
  FONT_SIZE = 'FONT_SIZE',
  COLOR = 'COLOR',
}

export interface ToolbarIconProps {
  type: ToolType;
  editor: Editor;
}

export const ToolbarIcon: React.FC<ToolbarIconProps> = ({ type, editor }) => {
  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor.isActive('bold'),
      orderedList: editor.isActive('orderedList'),
      bulletList: editor.isActive('bulletList'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      center: editor.isActive({ textAlign: 'center' }),
      left: editor.isActive({ textAlign: 'left' }),
    }),
  });

  /**
   * Holds the correct icon for the ToolType
   */
  const icon: JSX.Element = useMemo((): JSX.Element => {
    switch (type) {
      case ToolType.BOLD:
        return (
          <BoldIcon
            width={30}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editorState.bold ? 'active' : ''}
          />
        );

      case ToolType.ORDERED_LIST:
        return (
          <OrderedListIcon
            width={30}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editorState.orderedList ? 'active' : ''}
          />
        );

      case ToolType.LIST:
        return (
          <ListIcon
            width={30}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editorState.bulletList ? 'active' : ''}
          />
        );

      case ToolType.ITALIC:
        return (
          <ItalicIcon
            width={30}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editorState.italic ? 'active' : ''}
          />
        );

      case ToolType.UNDERLINE:
        return (
          <UnderlineIcon
            width={30}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editorState.underline ? 'active' : ''}
          />
        );

      case ToolType.CENTERED:
        return (
          <CenteredIcon
            width={30}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editorState.center ? 'active' : ''}
          />
        );

      case ToolType.LEFT:
        return (
          <LeftIcon
            width={30}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editorState.left ? 'active' : ''}
          />
        );

      case ToolType.FONT_SIZE:
        return (
          <select
            className="toolbar-icon__select"
            onChange={(e) => editor.chain().focus().setFontSize(`${e.target.value}px`).run()}
          >
            {[6, 8, 10, 12, 14, 16, 18].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        );

      case ToolType.COLOR:
        return (
          <input
            className="toolbar-icon__color"
            type="color"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        );
    }
  }, [type, editor, editorState]);

  return <div className="toolbar-icon">{icon}</div>;
};
